import type { InputTableQuerySpec } from "@selector/shared/graphql";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { CalloutIntent, DialogComponent, HStack, Modal, VStack } from "widgets";
import { useOverlay } from "widgets/hooks";

import {
  createSelectionModel,
  type ISelectionModel,
  type TGridSelModelIdentifier,
} from "@/app-utils/selection-model.ts";
import { GenomeSelect } from "@/modules/workflow/components/parameters/GenomeSelect.tsx";
import { createUniqueKeyFromTableSpec, getTableDataSlice } from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { DATATABLE_ID_TYPE, DATATABLE_PARENT_TYPE, ENTITY_TYPE } from "@/settings/settings.ts";
import ColumnSelectionButton from "@/ui-modules/core/ColumnSelectionButton.tsx";
import DataTableContainer from "@/ui-modules/core/grid/DataTableContainer.tsx";
import { VariantMutantGenomesSelectionActionButton } from "@/ui-modules/genome-viewer/VariantMutantGenomesSelectionForm.tsx";

interface ModalGridProps {
  isOpen: boolean;
  availableGenomesIds: number[];
  onClose: () => void;
  onSaveSelection: (options: {
    genomeId: number | null;
    restrictionVariantGenomesIds: number[];
    selectedVariantsIds: number[];
    isOrganismTable: boolean | null;
    groupId: number | null;
    rowFilter: string | undefined;
  }) => Promise<void>;
  defaultValue: {
    genomeId: number | null;
    restrictionVariantGenomesIds: number[];
    selectedVariantsIds: number[];
    isOrganismTable: boolean | null;
    groupId: number | null;
    restrictionRegion:
      | {
          contigId: number;
          range: {
            start: number;
            stop: number;
          } | null;
        }
      | null
      | undefined;
    rowFilter: string | undefined;
  };
}

export const VariantsModal = ({
  availableGenomesIds,
  isOpen,
  onClose,
  onSaveSelection,
  defaultValue: {
    genomeId,
    restrictionVariantGenomesIds: defaultRestriction,
    selectedVariantsIds: defaultSelected,
    isOrganismTable,
    groupId,
    restrictionRegion,
    rowFilter,
  },
}: ModalGridProps) => {
  const { overlayProps, state } = useOverlay({ isOpen });
  const [selectedGenomeId, setSelectedGenomeId] = useState<number>(genomeId ?? availableGenomesIds[0]);
  const [selectionsByGenome, setSelectionsByGenome] = useState<Record<number, ISelectionModel["selectedIds"]>>({
    [genomeId ?? availableGenomesIds[0]]: defaultSelected ?? [],
  });
  const [restrictionsByGenome, setRestrictionsByGenome] = useState<Record<number, number[]>>({
    [genomeId ?? availableGenomesIds[0]]: defaultRestriction ?? [],
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Derive selectedVariantsIds from selectionsByGenome
  const selectedVariantsIds = selectionsByGenome[selectedGenomeId] || [];

  // Derive restrictionVariantGenomesIds from restrictionsByGenome
  const restrictionVariantGenomesIds = useMemo(
    () => restrictionsByGenome[selectedGenomeId] || [],
    [restrictionsByGenome, selectedGenomeId],
  );

  const tableSpec: InputTableQuerySpec | undefined = useMemo(() => {
    if (!isOrganismTable) {
      return {
        idType: DATATABLE_ID_TYPE.GROUP,
        queryType: null,
        resultType: ENTITY_TYPE.VCF_VARIANT,
        id: groupId,
        restrictions: { variantGenomeIds: restrictionVariantGenomesIds, region: restrictionRegion },
        rowFilter,
      };
    }

    if (!selectedGenomeId) {
      return undefined;
    }

    return {
      idType: DATATABLE_ID_TYPE.ENTITY,
      queryType: DATATABLE_PARENT_TYPE.ORGANISM,
      resultType: ENTITY_TYPE.VCF_VARIANT,
      id: selectedGenomeId,
      restrictions: { variantGenomeIds: restrictionVariantGenomesIds, region: restrictionRegion },
      rowFilter,
    };
  }, [restrictionVariantGenomesIds, selectedGenomeId, groupId, isOrganismTable, restrictionRegion, rowFilter]);

  const tableRowFilter = useSelector<IAppState, string | undefined>((state) => {
    // group-based tables should never show up outside of playbook context
    const tableSpec = {
      idType: DATATABLE_ID_TYPE.ENTITY,
      queryType: DATATABLE_PARENT_TYPE.ORGANISM,
      resultType: ENTITY_TYPE.VCF_VARIANT,
    };

    return getTableDataSlice(state.tableData, tableSpec)?.filterExpression;
  });

  const handleUpdate = useCallback(
    (genomeIdToSave: number | null, selectedVariantsIds: TGridSelModelIdentifier[]) => {
      if ((isOrganismTable && genomeIdToSave == null) || (!isOrganismTable && groupId == null)) {
        setError("Unable to determine table context");
        return;
      }

      setError(null);
      setIsSaving(true);

      onSaveSelection({
        genomeId: isOrganismTable ? genomeIdToSave : null,
        groupId,
        isOrganismTable,
        restrictionVariantGenomesIds,
        selectedVariantsIds: selectedVariantsIds as number[],
        rowFilter: tableRowFilter,
      })
        .then(() => {
          onClose();
        })
        .catch((err) => {
          const message = err instanceof Error ? err.message : "Unable to save variants";
          setError(message);
        })
        .finally(() => {
          setIsSaving(false);
        });
    },
    [groupId, isOrganismTable, onClose, onSaveSelection, restrictionVariantGenomesIds, tableRowFilter],
  );

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes because on open it forces a re-render!
      // I know it looks weird, but that's the only way I found to make it work.
      // Otherwise onSelectionModelChanged returns no selection from the start and we're loosing the selection
      // at first open. As long as you keep the activity selected, it's ok afterwards.
      const currentGenomeId = genomeId ?? availableGenomesIds[0];
      setSelectedGenomeId(currentGenomeId);
      setRestrictionsByGenome({ [currentGenomeId]: defaultRestriction ?? [] });
      setSelectionsByGenome({ [currentGenomeId]: defaultSelected ?? [] });
    }
  }, [genomeId, availableGenomesIds, defaultRestriction, defaultSelected, isOpen]);

  if (!tableSpec) {
    return null;
  }

  return (
    <Modal state={state} {...overlayProps}>
      <DialogComponent
        fullscreen
        title="Select Variants"
        mainButton={{
          label: `Select ${selectedVariantsIds.length === 0 ? "All" : "(" + selectedVariantsIds.length + ")"}`,
          onPress: () => {
            setError(null);
            handleUpdate(selectedGenomeId, selectedVariantsIds);
          },
          isLoading: isSaving,
        }}
        actions={[
          <ColumnSelectionButton
            key="column-selection-button-placeholder"
            portalContext={createUniqueKeyFromTableSpec(tableSpec)}
          />,
        ]}
        closeFn={() => {
          setRestrictionsByGenome({});
          setSelectionsByGenome({});
          setIsSaving(false);
          setError(null);
          onClose();
        }}
        errorCallout={error ? { intent: CalloutIntent.Danger, message: error } : undefined}
        contentStyle={{ display: "grid", padding: 0, borderTop: "1px solid var(--ui-border)" }}
        infoComponent={
          isOrganismTable ? (
            <HStack spacing={10}>
              <GenomeSelect
                availableGenomesIds={availableGenomesIds}
                selectedId={selectedGenomeId}
                onChange={(id) => {
                  if (id !== selectedGenomeId) {
                    setError(null);
                    setSelectedGenomeId(id);
                  }
                }}
              />
              <VariantMutantGenomesSelectionActionButton
                onSave={(restrictions) => {
                  setRestrictionsByGenome((prev) => ({
                    ...prev,
                    [selectedGenomeId]: restrictions,
                  }));
                }}
                genomeId={selectedGenomeId}
                restrictionVariantGenomesIds={restrictionVariantGenomesIds}
              />
            </HStack>
          ) : null
        }
      >
        <VStack style={{ width: "100%", height: "100%" }}>
          {/* @ts-expect-error Reported missing properties of this component are actually not required */}
          <DataTableContainer
            key={createUniqueKeyFromTableSpec(tableSpec)}
            tableSpec={tableSpec}
            gridContext={{ tableSpec }}
            noRowsText="No variants found"
            onSelectionModelChanged={(selected) => {
              setError(null);
              setSelectionsByGenome((prev) => ({
                ...prev,
                [selectedGenomeId]: selected.selectedIds as number[],
              }));
            }}
            tableInitializationOptions={{
              initialSelectionModel: createSelectionModel(selectedVariantsIds),
              filterExpression: tableSpec.rowFilter ?? undefined,
            }}
          />
        </VStack>
      </DialogComponent>
    </Modal>
  );
};
